import { useGetNsuAuditHistoryQuery } from "@/services/conciliation/nsu";
import { formatToBRL } from "@/utils/formatToMoney";
import { ScrollArea } from "components/ui/scroll-area";
import { format } from "date-fns";
import { Calendar, DollarSign, Loader2, Receipt, User } from "lucide-react";
import React from "react";

import { HISTORY_LABELS } from "../utils/messages";

interface NsuHistoryContentProps {
  nsuId: number;
}

const formatDate = (value: string | null): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "dd/MM/yyyy HH:mm");
};

/**
 * Corpo do histórico de ajustes, extraído do antigo NsuHistoryPopover. Como agora abre
 * em modal, o componente só monta quando o usuário pede, o que mantém a busca sob demanda
 * que antes vinha do `skip: !isOpen` do Popover.
 */
export const NsuHistoryContent: React.FC<NsuHistoryContentProps> = ({ nsuId }) => {
  const { data: history, isFetching } = useGetNsuAuditHistoryQuery({ nsuId });

  if (isFetching) {
    return (
      <div className="flex items-center justify-center py-8 min-w-[320px]">
        <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
      </div>
    );
  }

  if (!Array.isArray(history) || history.length === 0) {
    return (
      <div className="text-sm text-muted-foreground p-4 text-center min-w-[320px]">
        {HISTORY_LABELS.EMPTY}
      </div>
    );
  }

  return (
    <ScrollArea className="h-72 w-full p-1 min-w-[320px]">
      <div className="space-y-6">
        {history.map(item => (
          <div
            key={item.id}
            className="border border-gray-100 rounded-lg p-3 bg-gray-50"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <Receipt className="w-3 h-3" /> {HISTORY_LABELS.ID} {item.id}
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <User className="w-3 h-3" /> {item.adjusted_by}
              </span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-600">
                {formatDate(item.created_at)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-500">
                {HISTORY_LABELS.ADJUSTMENT_VALUE}
              </span>
              <span className="font-bold text-sm text-[#402430]">
                {formatToBRL(Number(item.adjustment_amount))}
              </span>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};
